import dayjs from "dayjs";
import type { Options, Point } from "highcharts";
import ReactDOMServer from "react-dom/server";

import {
    combineConfigs,
    noLegendConfig,
    xAxisCrosshair,
    xAxisLabelsConfig,
    yAxisLabelsConfig,
    shortMonthAndYear,
    shortMonthDayAndYear,
} from "@similarweb/pro-logic-components";
import type { ColumnDefinition } from "@similarweb/ui-components";
import { ChartTooltip } from "@similarweb/ui-components";

import { translate } from "app/filters/i18nFilter";
import { minimumValueChangeFilter, percentageSignOnlyFilter } from "app/filters/ngFilters";
import { NA_VALUE } from "app/pages/digital-marketing/ad-intelligence/common/utils/utils";
import { GranularityNames } from "app/UtilitiesAndConstants/Constants/Moment";

import { EDataType } from "../../../advertisers/overview/components/types";
import type { ISeriesData } from "../hooks/usePerformanceOverTimeCompareData";
import {
    MAX_DATA_POINTS_FOR_ROTATION,
    xAxisFormatter,
    legendToFormatterMap,
    getTickPositionerConfig,
    getXAxisTickPositioner,
} from "../utils/graphUtils";

interface ITooltipData {
    displayName: string;
    fieldValue: string;
    originalValue: number;
    change: {
        value: string;
        isDecrease?: boolean;
        unsigned?: boolean;
    };
    items: {
        label: string;
        color: string;
    };
}
interface IChartPoint extends Point {
    change: number;
}

const metricTitleMap: Record<EDataType, string> = {
    get [EDataType.SPEND]() {
        return translate("ad.intelligence.overview.performance.over.time.widget.spend");
    },
    get [EDataType.IMPRESSIONS]() {
        return translate("ad.intelligence.overview.performance.over.time.widget.impressions");
    },
    get [EDataType.VISITS]() {
        return translate("ad.intelligence.overview.performance.over.time.widget.visits");
    },
    get [EDataType.PAGE_VIEWS]() {
        return translate("ad.intelligence.overview.performance.over.time.widget.pageViews");
    },
};

export const getCompareChartConfig = (
    granularity: GranularityNames,
    selectedMetric: EDataType,
    graphData: ISeriesData[],
    isPercentageMode = false,
): Options => {
    const dataPointsCount = Math.max(...graphData.map((series) => series.data.length), 0);
    const isMonthly = granularity === GranularityNames.MONTHLY;
    const isWeekly = granularity === GranularityNames.WEEKLY;
    const isSinglePoint = dataPointsCount === 1;

    return combineConfigs(
        {
            yAxisFormatter: (params: { value: number }) =>
                isPercentageMode ? `${Math.round(params.value)}%` : legendToFormatterMap[selectedMetric](params.value),
            xAxisFormatter: (params: { value: number }) => xAxisFormatter(params, isMonthly),
        },
        [
            noLegendConfig,
            yAxisLabelsConfig,
            xAxisLabelsConfig,
            xAxisCrosshair,
            {
                chart: {
                    height: 260,
                    margin: [4, 24, 24, 48],
                    spacing: [0, 0, 0, 0],
                },
                plotOptions: isPercentageMode
                    ? {
                          area: {
                              stacking: "percent",
                              fillOpacity: 1,
                              connectNulls: true,
                              lineWidth: 0.5,
                              marker: {
                                  enabled: false,
                              },
                          },
                          series: {
                              animation: false,
                          },
                      }
                    : undefined,
                yAxis: {
                    opposite: false,
                    ...(isPercentageMode
                        ? {
                              min: 0,
                              max: 100,
                              tickAmount: 5,
                          }
                        : getTickPositionerConfig(isSinglePoint)),
                },
                xAxis: {
                    tickPositioner: getXAxisTickPositioner,
                    labels: {
                        rotation: dataPointsCount > MAX_DATA_POINTS_FOR_ROTATION ? 22.5 : undefined,
                    },
                },
                tooltip: {
                    shared: true,
                    useHTML: true,
                    outside: true,
                    formatter: function () {
                        if (!this.points?.length) {
                            return "";
                        }

                        const rows: ITooltipData[] = (this.points as IChartPoint[]).map((point: IChartPoint) => {
                            const changeValue = point.change;

                            return {
                                displayName: point.series.name,
                                fieldValue: isPercentageMode
                                    ? percentageSignOnlyFilter()(point.percentage, 2)
                                    : legendToFormatterMap[selectedMetric](point.y),
                                originalValue: point.y,
                                change: {
                                    value:
                                        changeValue && changeValue !== 0
                                            ? minimumValueChangeFilter()(changeValue)
                                            : NA_VALUE,
                                    isDecrease: changeValue < 0,
                                    unsigned: changeValue === 0,
                                },
                                items: {
                                    label: point.series.name,
                                    color: point.color as string,
                                },
                            };
                        });

                        const columnDefinition: ColumnDefinition<ITooltipData>[] = [
                            {
                                key: "items",
                                title: "Advertiser",
                                selector: "items" as const,
                                align: "left",
                                size: "152px",
                                render: ({ items }: ITooltipData) => (
                                    <ChartTooltip.LabelCell
                                        label={items.label}
                                        backgroundColor={items.color}
                                    />
                                ),
                            },
                            {
                                key: "fieldValue",
                                title: isPercentageMode
                                    ? `${metricTitleMap[selectedMetric]} ${translate("ad.intelligence.overview.performance.over.time.widget.tooltip.share")}`
                                    : metricTitleMap[selectedMetric],
                                selector: "fieldValue" as const,
                                align: "right",
                                size: "66px",
                                render: ({ fieldValue }: ITooltipData) => (
                                    <ChartTooltip.ValueCell value={fieldValue.toString()} />
                                ),
                            },
                            {
                                key: "change",
                                title: "Change",
                                selector: "change" as const,
                                align: "right",
                                size: "80px",
                                render: ({ change }: ITooltipData) => (
                                    <ChartTooltip.ChangeCell
                                        value={change.value}
                                        isDecrease={change.isDecrease}
                                        unsigned={change.unsigned}
                                    />
                                ),
                            },
                        ];

                        let tooltipTitle = "";
                        const pointDate = dayjs.utc(this.x);
                        const dateFormat = isMonthly ? shortMonthAndYear : shortMonthDayAndYear;

                        if (isWeekly) {
                            const weekStart = pointDate;
                            const weekEnd = pointDate.add(6, "day");
                            tooltipTitle = translate("graph.tooltip.from.date.to.date", {
                                fromDate: weekStart.format(dateFormat),
                                toDate: weekEnd.format(dateFormat),
                            });
                        } else {
                            tooltipTitle = pointDate.format(dateFormat);
                        }

                        return ReactDOMServer.renderToString(
                            <ChartTooltip
                                width={336}
                                title={tooltipTitle}
                                columnDefinition={columnDefinition}
                                data={rows}
                            />,
                        );
                    },
                },
            },
        ],
    );
};
